import { JsonApiMeta } from "./jsonApiMeta";
import { UnionKeys } from "./types";

/**
 * Represents a JSON:API error response object.
 *
 * @see https://jsonapi.org/format/#error-objects
 */
export interface JsonApiErrorDocument {
  /** A meta object containing non-standard meta-information about the error response. */
  meta?: JsonApiMeta & { trace_id?: string };
  /** An array of error objects */
  errors: Array<JsonApiErrorObject>;
}

/**
 * Represents a JSON:API error object.
 *
 * @see https://jsonapi.org/format/#error-objects
 */
export interface JsonApiErrorObject {
  /** A unique identifier for this particular occurrence of the problem. */
  id?: string;
  links?: {
    /** A link that leads to further details about this particular occurrence of the problem. */
    about: string;
    /** A link that identifies the type of error that this particular error is an instance of. */
    type?: string;
  };
  /** The HTTP status code applicable to this problem. */
  status: string;
  /** An application-specific error code. */
  code?: string;
  /** A short, human-readable summary of the problem. Can be localized. */
  title?: string;
  /** A human-readable explanation specific to this occurrence of the problem. Can be localized. */
  detail?: string;
  /** An object containing references to the source of the error. */
  source?: {
    /**
     * A JSON Pointer [RFC6901] to the associated entity in the request document.
     *
     * @example "/data" for a primary data object
     * @example "/data/attributes/title" for a specific attribute
     */
    pointer?: string;
    /** A string indicating which URI query parameter caused the error. */
    parameter?: string;
    /** A string indicating which header caused the error. */
    header?: string;
  };
  /** A meta object containing non-standard meta-information about the error. */
  meta?: JsonApiMeta & { retryable?: boolean };
}

/**
 * Backwards-compatible type declaration for consumers that used JsonApiError as
 * the error-object shape before the response Error class was introduced.
 */
export interface JsonApiError extends JsonApiErrorObject {}

/**
 * Represents a JSON:API error response.
 *
 * @see https://jsonapi.org/format/#error-objects
 */
export class JsonApiError extends Error {
  /** A meta object containing non-standard meta-information about the error response. */
  public meta?: JsonApiMeta & { trace_id?: string };

  public errors: Array<JsonApiErrorObject>;

  constructor(body: JsonApiErrorDocument, options: { cause?: Error }) {
    super("JSON:API Error Response", { cause: options.cause });
    this.name = "JsonApiError";
    this.meta = body.meta;
    this.errors = body.errors;
  }
}

/**
 * Type guard to check if an error is a JsonApiErrorResponse
 * @param error The error to check
 * @returns True if the error is a JsonApiErrorResponse, false otherwise
 */
export function isJsonApiErrorResponse(error: unknown): error is JsonApiError {
  return error instanceof JsonApiError;
}

/**
 * Type guard to check if a body is a JsonApiErrorDocument
 * @param body The body to check
 * @returns True if the body is a JsonApiErrorDocument, false otherwise
 */
export function isJsonApiErrorDocument(
  body: unknown,
): body is JsonApiErrorDocument {
  if (
    typeof body === "object" &&
    body !== null &&
    "errors" in body &&
    Array.isArray((body as any).errors)
  ) {
    return true;
  }
  return false;
}

/**
 * Represents a JSON:API validation error response.
 *
 * @see https://jsonapi.org/format/#error-objects
 */
export class JsonApiValidationError<T> extends JsonApiError {
  public validationErrors: Partial<Record<UnionKeys<T>, string[]>> = {};

  constructor(body: JsonApiErrorDocument, options: { cause?: Error }) {
    super(body, options);

    for (const error of body.errors) {
      if (error.source && error.source.pointer) {
        const pointer = error.source.pointer;
        // Validation errors from backend have pointer like: /data/attributes/fieldName
        // When split by "/": ["", "data", "attributes", "fieldName"]
        // Index 3 contains the field name
        const attribute = pointer.split("/")[3] as UnionKeys<T> | undefined;
        if (!attribute) {
          // Skip if no valid attribute found
          continue;
        }

        if (!this.validationErrors[attribute]) {
          this.validationErrors[attribute] = [];
        }
        if (error.detail) {
          this.validationErrors[attribute]?.push(error.detail);
        }
      }
    }
  }
}

/**
 * Type guard to check if an error is a JsonApiValidationError
 * @param error The error to check
 * @returns True if the error is a JsonApiValidationError, false otherwise
 */
export function isJsonApiValidationError<T>(
  error: unknown,
): error is JsonApiValidationError<T> {
  return (
    error instanceof JsonApiError &&
    (error as JsonApiError).errors.some(
      (e) =>
        e.source !== undefined &&
        e.source.pointer !== undefined &&
        e.source.pointer.startsWith("/data/attributes/"),
    )
  );
}
